import { useMutation, useQueryClient } from "@tanstack/react-query";
import { makeRequest } from "../makeRequest";


type MutationOptions<TBody = any, TResponse = any> = {
  endpoint: string;
  method?: "POST" | "PUT" | "PATCH" | "DELETE";
  invalidateKeys?: any[];
  auth?: boolean;

  // optional query params to append to URL
  queryParams?: Record<string, string | number>;

  // 👇 hooks
  onSuccess?: (data: TResponse, variables: TBody) => void;
  onError?: (error: Error, variables: TBody, context: unknown) => void;
  onSettled?: () => void;
};

export function myMutation<TBody = any, TResponse = any>({
  endpoint,
  method = "POST",
  invalidateKeys = [],
  auth = true,
  onSuccess,
  onError,
  onSettled,
}: MutationOptions<TBody, TResponse>) {
  const queryClient = useQueryClient();

  return useMutation({
    // accept dynamic params via `mutate(variables, context?)`
    mutationFn: async (args: TBody & { queryParams?: Record<string, string | number> }) => {
      let url = endpoint;

      // append query params if provided dynamically in mutate call
      if (args.queryParams) {
        const params = new URLSearchParams(
          Object.entries(args.queryParams).map(([k, v]) => [k, String(v)])
        );
        url += `?${params.toString()}`;
      }

      // remove queryParams from body so it doesn't get sent in payload
      const { queryParams, ...body } = args;

      return makeRequest<TResponse>(url, {
        method,
        body: Object.keys(body).length ? body : undefined,
        auth,
      });
    },

    onSuccess: (data, variables) => {
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      onSuccess?.(data, variables);
    },

    onError: (error, variables, context) => {
      onError?.(error, variables, context);
    },


    onSettled: () => {
      onSettled?.();
    },
  });
}